import { createContext, useContext } from 'react';
import { getMeControllerGetMeQueryKey, useMeControllerGetMe } from '@/generated/api/endpoints/identity/identity';
import type { AttributeGroupDto, MeTenantDto, MeUserDto } from '@/generated/api/models';
import { useAuth } from '@/lib/auth/use-auth';

interface MeContextValue {
  user: MeUserDto | null;
  tenant: MeTenantDto | null;
  permissions: string[];
  registry: AttributeGroupDto[];
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, currentTenant } = useAuth();

  // /me only makes sense once both a session and a resolved tenant exist;
  // keyed by tenant so it refetches on a subdomain switch.
  const { data } = useMeControllerGetMe({
    query: {
      queryKey: [...getMeControllerGetMeQueryKey(), currentTenant?.id],
      enabled: isAuthenticated && currentTenant !== null,
      // No route boundary exists above this provider to catch a throw-on-403;
      // a denial is the auth state machine's business, not this screen's.
      throwOnError: false,
    },
  });

  const me = data?.data ?? null;

  const value: MeContextValue = {
    user: me?.user ?? null,
    tenant: me?.tenant ?? null,
    permissions: me?.permissions ?? [],
    registry: me?.registry ?? [],
  };

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error('useMe must be used within MeProvider');
  }
  return ctx;
}
